import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, Clock, ArrowRight, Filter, BookOpen, Users, Loader2 } from 'lucide-react';
import '../../../assests/css/customer/Blog.css';
import MyNavbar from '../../../components/Customer/Mynavbar/MyNavbar';
import Footer from '../../../components/Customer/Footer/Footer';
import AppContext from '../../../context/AppContext';
import axios from 'axios';

const CATEGORY_ICONS = {
  'product reviews': '⭐',
  'business tips': '💼',
  'technology': '🚀',
  'marketing': '📈',
  'uncategorized': '📄'
};

const DEFAULT_CATEGORY = 'Uncategorized';

const BlogPage = () => {
  const navigate = useNavigate();
  const { baseUrl, baseUrlForFiles } = useContext(AppContext);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${baseUrl}/admin/blog/getAllBlogs`)
      .then((res) => {
        if (res.data.success) {
          setBlogs(res.data.data);
        } else {
          setError('Blogs load nahi ho sake.');
        }
      })
      .catch(() => setError('Server se connection nahi ho raha.'))
      .finally(() => setLoading(false));
  }, []);

  const handleCardClick = (id) => {
    navigate(`/blog-detail/${id}`);
    window.scrollTo(0, 0);
  };

  const getImageUrl = (img) => {
    if (!img) return 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=600&fit=crop&q=80';
    if (img.startsWith('http')) return img;
    return `${baseUrlForFiles}/${img}`;
  };

  // mlcategory null/empty ho to "Uncategorized" fallback
  const getCategoryLabel = (val) => {
    const trimmed = (val || '').toString().trim();
    return trimmed || DEFAULT_CATEGORY;
  };

  const normalize = (val) => getCategoryLabel(val).toLowerCase();

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  // Categories dynamically blogs se banti hain, null category "Uncategorized" ban jati hai
  const categories = React.useMemo(() => {
    const seen = new Map();
    blogs.forEach((b) => {
      const label = getCategoryLabel(b.mlcategory);
      const key = label.toLowerCase();
      if (!seen.has(key)) seen.set(key, label);
    });

    const dynamicCats = Array.from(seen.entries()).map(([key, name]) => ({
      name,
      icon: CATEGORY_ICONS[key] || '📄'
    }));

    return [{ name: 'All', icon: '📚' }, ...dynamicCats];
  }, [blogs]);

  const heroArticle      = blogs[0] || null;
  const featuredArticles = blogs.slice(1, 4);

  // Edge-case fix: agar total blogs kam hain (<=3), to hero wala blog bhi
  // "Latest Articles" mein dikhe — warna sirf 1-2 blog hone par list khali reh jati thi.
  const latestArticles = blogs.length > 3 ? blogs.slice(1) : blogs;

  const filteredArticles = selectedCategory === 'All'
    ? latestArticles
    : latestArticles.filter(a => normalize(a.mlcategory) === normalize(selectedCategory));

  if (loading) {
    return (
      <>
        <MyNavbar />
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', gap: '12px' }}>
          <Loader2 style={{ animation: 'spin 1s linear infinite', width: 28, height: 28 }} />
          <span style={{ fontSize: '16px', color: 'var(--color-text-secondary)' }}>Blogs load ho rahe hain...</span>
        </div>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <MyNavbar />
        <div style={{ textAlign: 'center', padding: '80px 20px' }}>
          <p style={{ fontSize: '18px', color: 'var(--color-text-secondary)' }}>{error}</p>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <MyNavbar />
      <br />
      <div className="eblog-page">

        {/* SECTION 1: Hero */}
        {heroArticle && (
          <section className="eblog-hero-section">
            <div className="eblog-container">
              <div className="eblog-hero-content">
                <div className="eblog-hero-text">
                  <div className="eblog-hero-badge">
                    <TrendingUp className="eblog-badge-icon" />
                    <span>Featured Article</span>
                  </div>
                  <h1 className="eblog-hero-title">{heroArticle.title}</h1>
                  <p className="eblog-hero-description">{heroArticle.excerpt}</p>
                  <div className="eblog-hero-meta">
                    <div className="eblog-author-info">
                      {heroArticle.author_avatar && (
                        <img src={getImageUrl(heroArticle.author_avatar)} alt={heroArticle.author_name} className="eblog-author-avatar" />
                      )}
                      <div>
                        <div className="eblog-author-name">{heroArticle.author_name || 'Admin'}</div>
                        <div className="eblog-author-role">{heroArticle.author_role || ''}</div>
                      </div>
                    </div>
                    <div className="eblog-hero-stats">
                      <div className="eblog-stat-item">
                        <Clock className="eblog-stat-icon" />
                        <span>{heroArticle.read_time || '5 min read'}</span>
                      </div>
                      <div className="eblog-stat-item">
                        <Users className="eblog-stat-icon" />
                        <span>{heroArticle.views || '0'} views</span>
                      </div>
                    </div>
                  </div>
                  <button className="eblog-cta-button" onClick={() => handleCardClick(heroArticle.id)}>
                    <BookOpen className="eblog-button-icon" />
                    <span>Read Full Article</span>
                    <ArrowRight className="eblog-button-arrow" />
                  </button>
                </div>

                <div className="eblog-hero-image-wrapper">
                  <div className="eblog-hero-visual-container">
                    <div className="eblog-hero-visual-card">
                      <div className="eblog-hero-shimmer"></div>
                      <div className="eblog-hero-animated-bg"></div>
                      <div className="eblog-hero-shape eblog-hero-shape-1"></div>
                      <div className="eblog-hero-shape eblog-hero-shape-2"></div>
                      <div className="eblog-hero-shape eblog-hero-shape-3"></div>
                      <div className="eblog-hero-dots-pattern"></div>
                      <div className="eblog-hero-content-wrapper">
                        <div className="eblog-hero-header-section">
                          <div className="eblog-hero-brand-badge">
                            <span className="eblog-hero-sparkle">✨</span>
                            <span>E-COMMERCE BLOG</span>
                          </div>
                          <h1 className="eblog-hero-main-title">Discover Stories That Inspire</h1>
                          <p className="eblog-hero-subtitle">Expert insights, product reviews, and success stories from the world of online business</p>
                        </div>
                        <div className="eblog-hero-visual-grid">
                          <div className="eblog-hero-visual-box">
                            <div className="eblog-hero-icon-circle">📚</div>
                            <div className="eblog-hero-box-title">Articles</div>
                            <div className="eblog-hero-box-count">{blogs.length}+</div>
                          </div>
                          <div className="eblog-hero-visual-box">
                            <div className="eblog-hero-icon-circle">👥</div>
                            <div className="eblog-hero-box-title">Readers</div>
                            <div className="eblog-hero-box-count">50K+</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* SECTION 2: Editor's Picks */}
        {featuredArticles.length > 0 && (
          <section className="eblog-featured-section">
            <div className="eblog-container">
              <div className="eblog-section-header">
                <div>
                  <h2 className="eblog-section-title">Editor's Picks</h2>
                  <p className="eblog-section-subtitle">Hand-selected articles by our expert team</p>
                </div>
              </div>
              <div className="eblog-featured-grid">
                {featuredArticles.map((article) => (
                  <article key={article.id} className="eblog-featured-card" onClick={() => handleCardClick(article.id)}>
                    <div className="eblog-featured-image-container">
                      <img src={getImageUrl(article.image)} alt={article.title} className="eblog-featured-image" />
                      <div className="eblog-featured-overlay">
                        <span className="eblog-category-tag">{getCategoryLabel(article.mlcategory)}</span>
                      </div>
                    </div>
                    <div className="eblog-featured-body">
                      <h3 className="eblog-featured-title">{article.title}</h3>
                      <p className="eblog-featured-excerpt">{article.excerpt}</p>
                      <div className="eblog-featured-footer">
                        <div className="eblog-article-meta">
                          <span className="eblog-meta-author">{article.author_name || 'Admin'}</span>
                          <span className="eblog-meta-dot">•</span>
                          <span>{formatDate(article.created_at)}</span>
                        </div>
                        <span className="eblog-read-time">{article.read_time || '5 min'}</span>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* SECTION 3: Latest Articles */}
        <section className="eblog-latest-section">
          <div className="eblog-container">
            <div className="eblog-section-header">
              <div>
                <h2 className="eblog-section-title">Latest Articles</h2>
                <p className="eblog-section-subtitle">Stay updated with our newest content</p>
              </div>
            </div>

            <div className="eblog-filter-container">
              <div className="eblog-filter-label">
                <Filter className="eblog-filter-icon" />
                <span>Filter by category:</span>
              </div>
              <div className="eblog-category-pills">
                {categories.map((cat) => (
                  <button
                    key={cat.name}
                    onClick={() => setSelectedCategory(cat.name)}
                    className={`eblog-category-pill ${selectedCategory === cat.name ? 'active' : ''}`}
                  >
                    <span className="eblog-pill-icon">{cat.icon}</span>
                    <span>{cat.name}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="eblog-articles-grid">
              {filteredArticles.length === 0 ? (
                <p style={{ color: 'var(--color-text-secondary)', gridColumn: '1/-1', textAlign: 'center', padding: '40px 0' }}>
                  Is category mein koi article nahi mila.
                </p>
              ) : (
                filteredArticles.map((article) => (
                  <article key={article.id} className="eblog-article-card">
                    <div className="eblog-article-image-wrapper">
                      <img src={getImageUrl(article.image)} alt={article.title} className="eblog-article-image" />
                      <div className="eblog-article-badge">{getCategoryLabel(article.mlcategory)}</div>
                    </div>
                    <div className="eblog-article-body">
                      <h3 className="eblog-article-title">{article.title}</h3>
                      <p className="eblog-article-excerpt">{article.excerpt}</p>
                      <div className="eblog-article-footer">
                        <span className="eblog-article-date">{formatDate(article.created_at)}</span>
                        <span className="eblog-article-read-time">{article.read_time || '5 min'}</span>
                      </div>
                    </div>
                    <div className="eblog-article-hover-overlay">
                      <button className="eblog-read-now-btn" onClick={() => handleCardClick(article.id)}>
                        <span>Read Now</span>
                        <ArrowRight className="eblog-arrow-icon" />
                      </button>
                    </div>
                  </article>
                ))
              )}
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default BlogPage;